/**
 * MapPage.tsx — Mapa GIS (ADR-016 · F1)
 *
 * Lotes y Módulos (F1) se pintan siempre juntos sobre el mismo `MapView`
 * compartido — el lote es el contenedor, el módulo la subdivisión coloreada
 * por estado dentro de él; el panel lateral los agrupa en un árbol Lote →
 * Módulos en vez del selector de capa que había antes. Los módulos se filtran
 * por el proyecto/temporada activo del TopBar.
 *
 * Toda la lógica de Leaflet vive ahora en `components/shared/MapView` — esta
 * página ya no importa `leaflet` directamente.
 */

import { useFieldErrors } from '../hooks/useFieldErrors';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Text, Spinner, Button, Badge, Switch, makeStyles, tokens,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Field, Input, Dropdown, Option, Checkbox, Select,
} from '@fluentui/react-components';
import {
  Location24Regular, Edit24Regular, Save24Regular,
  Delete24Regular, Dismiss24Regular, AddRegular, EditRegular,
  ChevronDownRegular, ChevronRightRegular, ChevronLeftRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNdviSettings } from '../hooks/useNdviSettings';
import { latestNdviSceneDate, formatSceneDate } from '../utils/ndvi-scene-date';
import { computeDynamicNdviRange, type NdviScaleMode, type NdviRange } from '../utils/ndvi-color';
import { isNdviDisabledError } from '../api/ndvi-settings.api';
import { useParams, useSearchParams } from 'react-router';
import { orderedModules } from '../utils/module-tree';
import { boundaryToVertices } from '../utils/polygon-edit';
import { MapView, STATUS_COLORS, geodesicAreaHa, vertexColor, type MapFeature, type FeatureStatus } from '../components/shared/MapView';
import { fetchLotesForMap, patchLoteBoundary } from '../api/dashboard.api';
import {
  fetchModules, fetchProjects, createModule, updateModule, patchModuleBoundary, deleteModule,
  fetchCrops, fetchModulesNdviBatch, type FarmModule,
} from '../api/terrain.api';
import { fetchLatestStages } from '../api/crop.api';
import { fetchRanchos } from '../api/rancho.api';
import { ColorPickerField } from '../components/shared/ColorPickerField';
import { LoteFormDialog } from './FieldPage';
import { fetchScopedConfig } from '../api/config-scope.api';
import { useProjectStore } from '../store/project.store';
import { useRanchoStore } from '../store/rancho.store';
import { useT } from '../i18n';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePermission } from '../hooks/usePermission';
import { useRanchoToday, useFollowingToday } from '../hooks/useRanchoToday';
import type { GeoJSONPolygon } from '../types/api.types';
import {
  FILL_THEMES, DEFAULT_FILL_THEME, fillColorForTheme, fillThemeLegend, isFillTheme, type FillTheme,
} from '../utils/map-fill-theme';
import { loadLabelPrefs, saveLabelPrefs, type LabelFields } from '../utils/map-labels';
import { buildCropPatternMap, CLOSED_CYCLE_DASH } from '../utils/crop-checkerboard';
import { isClosedCycleLook } from '../utils/crop-cycle-map';
import { cropLabel } from '../utils/crop-label';

const useStyles = makeStyles({
  root:    { display: 'flex', height: 'calc(100vh - 120px)', gap: '0', overflow: 'hidden', borderRadius: tokens.borderRadiusMedium },
  sidebar: {
    width: '280px', flexShrink: '0',
    background: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  // Colapsado: solo queda el botón de expandir (primer hijo); el resto del panel se oculta.
  sidebarCollapsed: {
    width: '44px',
    alignItems: 'center',
    '& > *:not(:first-child)': { display: 'none' },
  },
  sidebarToggle: { margin: '8px 0 0 8px', alignSelf: 'flex-start' },
  sidebarHeader: {
    padding: '16px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  list: { flex: '1', overflowY: 'auto' },
  item: {
    padding: '12px 16px', cursor: 'pointer',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex', flexDirection: 'column', gap: '2px',
    ':hover': { background: tokens.colorNeutralBackground2 },
  },
  itemActive: { background: tokens.colorBrandBackground2 },
  moduleItem: {
    padding: '8px 16px 8px 32px', cursor: 'pointer',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex', flexDirection: 'column', gap: '2px',
    ':hover': { background: tokens.colorNeutralBackground2 },
  },
  mapContainer: { flex: '1', position: 'relative' as const },
  mapEl:        { width: '100%', height: '100%' },
  toolbar: {
    position: 'absolute' as const, bottom: '12px', right: '12px', zIndex: '1000',
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 14px', boxShadow: tokens.shadow8,
    display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '200px',
  },
  toolbarButtons: { display: 'flex', flexDirection: 'column', gap: '6px', '& > button': { justifyContent: 'flex-start' } },
  legend: {
    position: 'absolute' as const, bottom: '12px', left: '12px', zIndex: '1000',
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '10px 14px', boxShadow: tokens.shadow8,
    display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px',
  },
  legendRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  legendDot: { width: '12px', height: '12px', borderRadius: '3px', flexShrink: 0 },
  infoBar: {
    position: 'absolute' as const, bottom: '12px', left: '50%',
    transform: 'translateX(-50%)', zIndex: '1000',
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px 16px', boxShadow: tokens.shadow8,
    display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px',
  },
  errorBox: {
    background: tokens.colorStatusDangerBackground1,
    color: tokens.colorStatusDangerForeground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px 16px', fontSize: '13px', margin: '8px',
  },
  inviteCtaBox: {
    background: tokens.colorStatusSuccessBackground1,
    color: tokens.colorStatusSuccessForeground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px 16px', fontSize: '13px', margin: '8px',
    display: 'flex', alignItems: 'center', gap: '12px',
  },
  empty: { padding: '24px 16px', color: tokens.colorNeutralForeground3, fontSize: '13px' },
  infoCard: {
    margin: '8px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '6px',
    background: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  infoRow: { display: 'flex', justifyContent: 'space-between', gap: '8px', color: tokens.colorNeutralForeground2 },
  mono: { fontFamily: 'monospace' },
  panelSection: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  panelTitle: { color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.04em' },
  dateSliderBar: {
    position: 'absolute' as const, top: '12px', left: '12px', zIndex: '1000',
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 16px', boxShadow: tokens.shadow8,
    display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px',
    width: 'min(560px, calc(100% - 24px))',
  },
});

const LEGEND_ORDER: FeatureStatus[] = ['PREPARACION', 'NORMAL', 'ATENCION', 'CRITICO', 'COSECHANDO'];

// Espejo de MAX_REASONABLE_BOUNDARY_HA en aws-serverless/src/utils/geo.ts —
// el backend es la guardia real, esto es solo UX inmediata.
const MAX_REASONABLE_BOUNDARY_HA = 10_000;

export function FarmModuleFormDialog({
  open, onClose, loteId, loteName, initial, activeProjectId, loteModules = [], defaultParentId,
}: { open: boolean; onClose: () => void; loteId: string; loteName?: string; initial?: FarmModule; activeProjectId: string | null; loteModules?: FarmModule[]; defaultParentId?: string }) {
  const t = useT();
  const qc = useQueryClient();
  const isEdit = !!initial;
  const [moduleCode, setModuleCode] = useState(initial?.moduleCode ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [displayColor, setDisplayColor] = useState<string | null>(initial?.displayColor ?? null);
  const [parentId, setParentId] = useState(initial?.parentModuleId ?? defaultParentId ?? '');
  const [attendanceZone, setAttendanceZone] = useState(!!initial?.attendanceZone);
  // Un solo nivel: solo zonas de primer nivel pueden ser padre, y una zona con
  // hijas no puede anidarse.
  const hasChildren = !!initial && loteModules.some(m => m.parentModuleId === initial.moduleId);
  const parentOptions = loteModules.filter(m => !m.parentModuleId && m.moduleId !== initial?.moduleId);
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  // Reinicia el formulario cada vez que el diálogo se (re)abre o cambia la zona editada.
  useEffect(() => {
    if (!open) return;
    setModuleCode(initial?.moduleCode ?? '');
    setName(initial?.name ?? '');
    setDisplayColor(initial?.displayColor ?? null);
    setParentId(initial?.parentModuleId ?? defaultParentId ?? '');
    setAttendanceZone(!!initial?.attendanceZone);
    setError(''); fe.clear();
  }, [open, initial?.moduleId, initial?.moduleCode, initial?.name, initial?.displayColor, initial?.attendanceZone]);

  const createMut = useMutation({
    mutationFn: () => createModule({
      loteId,
      moduleCode: moduleCode.trim(),
      name: name.trim() || undefined,
      projectId: activeProjectId ?? undefined,
      displayColor: displayColor ?? undefined,
      parentModuleId: parentId || undefined,
      attendanceZone: attendanceZone || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['modules-map-at-date'] });
      qc.invalidateQueries({ queryKey: ['investor-modules'] });
      onClose();
    },
    onError: (e: any) => {
      const status = e?.response?.status;
      setError(status === 409 ? t.terrain.duplicateModuleCode : t.terrain.createModuleError);
    },
  });

  const updateMut = useMutation({
    mutationFn: () => updateModule(initial!.moduleId, {
      name: name.trim() || undefined,
      // null = quitar el color elegido
      displayColor,
      // null = volver a primer nivel
      parentModuleId: parentId || null,
      attendanceZone,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['modules-map-at-date'] });
      qc.invalidateQueries({ queryKey: ['investor-modules'] });
      onClose();
    },
    onError: () => setError(t.terrain.updateModuleError),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteModule(initial!.moduleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['modules-map-at-date'] });
      qc.invalidateQueries({ queryKey: ['investor-modules'] });
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error === 'MODULE_HAS_CHILDREN' ? t.terrain.deleteModuleHasChildren : t.terrain.deleteModuleError),
  });

  const mut = isEdit ? updateMut : createMut;

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '440px' }}>
        <DialogBody>
          <DialogTitle>{isEdit ? `${t.terrain.editModuleTitle} — ${initial!.moduleCode}` : t.terrain.newModuleTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              {loteName && (
                <Field label={t.terrain.fieldLoteReadonly}>
                  <Input value={loteName} disabled />
                </Field>
              )}
              <Field label={t.terrain.fieldModuleCode} hint={isEdit ? t.terrain.fieldModuleCodeReadonlyHint : undefined} {...fe.props('moduleCode')}>
                <Input value={moduleCode} onChange={(_, d) => { setModuleCode(d.value); fe.clear('moduleCode'); }} placeholder={t.terrain.fieldModuleCodePlaceholder} disabled={isEdit || mut.isPending} />
              </Field>
              <Field label={t.terrain.fieldModuleName}>
                <Input value={name} onChange={(_, d) => setName(d.value)} disabled={mut.isPending} />
              </Field>
              {!hasChildren && parentOptions.length > 0 && (
                <Field label={t.terrain.fieldParentModule}>
                  <Select value={parentId} onChange={(_, d) => setParentId(d.value)} disabled={mut.isPending}>
                    <option value="">{t.terrain.parentModuleNone}</option>
                    {parentOptions.map(m => <option key={m.moduleId} value={m.moduleId}>{m.moduleCode}{m.name ? ` — ${m.name}` : ''}</option>)}
                  </Select>
                </Field>
              )}
              <Field hint={t.terrain.attendanceZoneHint}>
                <Checkbox checked={attendanceZone} onChange={(_, d) => setAttendanceZone(!!d.checked)} label={t.terrain.attendanceZoneLabel} disabled={mut.isPending} />
              </Field>
              <Text size={200}>{t.terrain.cropAssignedInCultivo}</Text>
              <Field label={t.common.colorLabel}>
                <ColorPickerField value={displayColor} onChange={setDisplayColor} disabled={mut.isPending} />
              </Field>
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            {isEdit && (
              <Button
                appearance="subtle"
                icon={<Delete24Regular />}
                style={{ marginRight: 'auto', color: tokens.colorStatusDangerForeground1 }}
                disabled={mut.isPending || deleteMut.isPending}
                onClick={() => { if (window.confirm(`${t.terrain.deleteModuleConfirm} ${initial!.moduleCode}${initial!.name ? ` — ${initial!.name}` : ''}?`)) { setError(''); deleteMut.mutate(); } }}
              >
                {t.terrain.deleteModuleBtn}
              </Button>
            )}
            <Button appearance="secondary" onClick={onClose} disabled={mut.isPending}>{t.ranchosAdmin.cancel}</Button>
            <Button
              appearance="primary"
              onClick={() => { setError(''); if (!fe.validate({ moduleCode: !moduleCode.trim() && t.common.validation.fieldRequired })) return; mut.mutate(); }}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {isEdit ? t.terrain.saveModuleBtn : t.terrain.createModuleBtn}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}


const NDVI_SCALE_KEY = 'agrovid.map.ndviScale';
/** Lee la escala guardada; cualquier fallo de almacenamiento cae a la fija. */
function loadNdviScale(): NdviScaleMode {
  try {
    return localStorage.getItem(NDVI_SCALE_KEY) === 'DYNAMIC' ? 'DYNAMIC' : 'FIXED';
  } catch {
    return 'FIXED';
  }
}
function saveNdviScale(mode: NdviScaleMode): void {
  try {
    localStorage.setItem(NDVI_SCALE_KEY, mode);
  } catch {
    // Sin almacenamiento la preferencia dura solo la sesión de la página.
  }
}

export function MapPage() {
  const s = useStyles();
  const t = useT();
  usePageTitle(t.pageTitle.map);
  const f = t.map;
  const qc = useQueryClient();
  const { tenantId } = useParams<{ tenantId: string }>();
  // F0 — self-register-onboarding Task 16: si venimos encadenados desde
  // "crear potrero" en FieldPage (`?loteId=...&draw=1`), preseleccionamos
  // ese lote y entramos a drawMode automáticamente en cuanto cargue la
  // lista de lotes — sin esto el usuario tendría que buscar el lote nuevo
  // a mano en el panel lateral y volver a pulsar "Dibujar".
  const [searchParams, setSearchParams] = useSearchParams();
  const autoDrawConsumedRef = useRef(false);
  // La invitación de usuarios tras guardar la geocerca se ocultó a propósito (no mezclar
  // con usuarios por ahora); el CTA y su i18n (map.inviteAfterGeofence*) quedan sin uso.

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState(false);
  const [recenterTo, setRecenterTo] = useState<{ center: [number, number]; bounds?: [[number, number], [number, number]] } | null>(null);
  const [drawPoints, setDrawPoints] = useState<[number, number][]>([]);
  // Vertices guardados con los que arranca MapView al editar una geocerca existente.
  const [editSeed, setEditSeed] = useState<[number, number][] | undefined>(undefined);
  const [satellite, setSatellite] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [moduleFormOpen, setModuleFormOpen] = useState(false);
  const [editModuleId, setEditModuleId] = useState<string | null>(null);
  const [editLoteOpen, setEditLoteOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [collapsedLotes, setCollapsedLotes] = useState<Set<string>>(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pendingExitAction, setPendingExitAction] = useState<(() => void) | null>(null);

  // Slider de fecha (Fase mapa avanzado) — recalcula NDVI y cultivo vigente
  // de cada módulo para la fecha elegida. sliderInput es lo que arrastra el
  // <input type="range"> en vivo; selectedDate es la versión debounced que
  // dispara las queries, para no golpear el backend en cada pixel de arrastre.
  // "Hoy" en la zona horaria del rancho (no UTC).
  const todayRanchoFn = useRanchoToday();
  const today = todayRanchoFn();
  // Se reajusta cuando llega la zona del rancho (salvo que el usuario ya movió el slider).
  const { selected: selectedDate, input: sliderInput, change: handleSliderChange } = useFollowingToday(today);
  // Hoy se lee el espejo vigente; otra fecha se reconstruye desde los ciclos.
  const isHistoric = selectedDate !== today;
  // Tema de relleno (uno a la vez); el borde siempre marca el cultivo.
  const [fillTheme, setFillTheme] = useState<FillTheme>(DEFAULT_FILL_THEME);
  // Escala del NDVI: fija (por defecto) o dinámica (rango observado). Preferencia
  // por navegador; sin try/catch fallaría en navegadores con el almacenamiento bloqueado.
  const [ndviScale, setNdviScale] = useState<NdviScaleMode>(() => loadNdviScale());
  const changeNdviScale = (mode: NdviScaleMode) => {
    setNdviScale(mode);
    saveNdviScale(mode);
  };
  // `ndvi.enabled` (empresa/rancho, apagado por defecto): si ningún rancho lo tiene
  // habilitado se oculta la capa NDVI; si falla la lectura, manda el 403 del backend.
  const ndviSettings = useNdviSettings();
  const ndviKnownOff = ndviSettings.anyEnabled === false;
  const ndviLayerOn = fillTheme === 'ndvi' && !ndviKnownOff;
  // Capa de etiquetas (independiente del tema) — persistida en localStorage.
  const [labelPrefs, setLabelPrefs] = useState(() => loadLabelPrefs());
  const updateLabelPrefs = (next: { enabled: boolean; fields: LabelFields }) => {
    setLabelPrefs(next);
    saveLabelPrefs(next);
  };

  const canWriteTerrain = usePermission('lotes:write');

  const { activeProjectId, setActiveProject } = useProjectStore();
  const { data: projects = [] } = useQuery({
    queryKey: ['projects-map'],
    queryFn: fetchProjects,
  });

  // Sin esto, activeProjectId queda en null hasta que el usuario elige uno
  // en el TopBar — y la query de módulos abajo nunca se dispara (H12).
  // Si el proyecto guardado (localStorage) ya no existe en la lista, también se
  // reemplaza; si no, el mapa queda sin zonas y sin botón "Crear zona".
  useEffect(() => {
    if (projects.length === 0) return;
    if (!activeProjectId || !projects.some(p => p.projectId === activeProjectId)) {
      setActiveProject(projects[0].projectId, projects[0].name);
    }
  }, [projects, activeProjectId, setActiveProject]);

  // Rango del slider de fecha: desde el inicio del proyecto activo hasta hoy —
  // más atrás no hay historial de cultivo/NDVI que mostrar.
  const activeProject = projects.find(p => p.projectId === activeProjectId);

  // Selector de rancho de la barra superior: el mapa solo debe mostrar los lotes
  // del rancho activo (antes mostraba los de todos los ranchos).
  const { activeRanchoId } = useRanchoStore();
  const { data: lotes = [], isLoading: loadingLotes } = useQuery({
    queryKey: ['lotes-map', activeRanchoId, isHistoric ? selectedDate : 'today'],
    queryFn: () => fetchLotesForMap(activeRanchoId, isHistoric ? selectedDate : undefined),
    placeholderData: (prev) => prev,
  });

  const { data: modules = [], isLoading: loadingModules } = useQuery({
    queryKey: ['modules-map-at-date', activeRanchoId, isHistoric ? selectedDate : 'today'],
    // Se muestran todas las zonas del rancho, sin filtrar por proyecto: una zona
    // sin proyecto o de otro proyecto dejaba el árbol de lotes sin sus zonas.
    queryFn: () => fetchModules({ ranchoId: activeRanchoId, date: isHistoric ? selectedDate : undefined }),
    // Evita que el checkerboard/NDVI parpadeen mientras se arrastra el slider —
    // se sigue viendo la respuesta anterior hasta que llega la nueva.
    placeholderData: (prev) => prev,
  });

  // Hasta el inicio del proyecto, o la primera fecha registrada de una zona; si no hay
  // ninguna anterior a hoy, un año atrás para poder ver el histórico.
  const sliderMinDate = useMemo(() => {
    const yearAgo = new Date(`${today}T00:00:00Z`);
    yearAgo.setUTCDate(yearAgo.getUTCDate() - 365);
    const candidates = [
      activeProject?.startDate,
      ...modules.flatMap(m => [m.createdAt?.slice(0, 10), m.plantingDate?.slice(0, 10)]),
    ].filter((d): d is string => !!d && d < today);
    return candidates.length ? candidates.reduce((a, b) => (b < a ? b : a)) : yearAgo.toISOString().slice(0, 10);
  }, [activeProject?.startDate, modules, today]);

  const { data: cropsCatalog = [] } = useQuery({
    queryKey: ['crops-catalog'],
    queryFn: fetchCrops,
  });

  const { data: ndviBatch, error: ndviBatchError } = useQuery({
    queryKey: ['modules-ndvi-batch', activeProjectId, selectedDate],
    queryFn: () => fetchModulesNdviBatch({ projectId: activeProjectId ?? undefined, date: selectedDate }),
    enabled: ndviLayerOn && !!activeProjectId,
    placeholderData: (prev) => prev,
    retry: false,
  });
  const ndviSceneDate = latestNdviSceneDate(ndviBatch?.results);
  // Rango para la escala dinámica; `null` (fija, o sin rango válido) = escala fija.
  const ndviRange: NdviRange | null = ndviLayerOn && ndviScale === 'DYNAMIC'
    ? computeDynamicNdviRange((ndviBatch?.results ?? []).map(r => r.ndviStats?.mean))
    : null;
  const ndviNotice = fillTheme === 'ndvi' && (ndviKnownOff || isNdviDisabledError(ndviBatchError));

  // Tema "Etapa fenológica": última etapa por lote (las zonas heredan la de su lote).
  const { data: stagesByLote = {} } = useQuery({
    queryKey: ['phenology-latest-stage', activeRanchoId],
    queryFn: () => fetchLatestStages(activeRanchoId),
    enabled: fillTheme === 'stage',
  });

  const cropPatternMap = useMemo(() => buildCropPatternMap(cropsCatalog), [cropsCatalog]);

  const { data: tenantConfigData } = useQuery({
    queryKey: ['tenant-config', tenantId],
    queryFn: () => tenantId ? fetchScopedConfig('TENANT', tenantId) : Promise.resolve(null),
    enabled: !!tenantId,
  });

  const saveMutation = useMutation({
    mutationFn: ({ loteId, boundary }: { loteId: string; boundary: GeoJSONPolygon }) =>
      patchLoteBoundary(loteId, boundary),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lotes-map'] });
      setSaveError(null);
      exitDrawMode();
    },
    onError: (e: Error) => setSaveError(e.message ?? f.saveError),
  });

  const saveModuleBoundaryMutation = useMutation({
    mutationFn: ({ moduleId, boundary }: { moduleId: string; boundary: GeoJSONPolygon }) =>
      patchModuleBoundary(moduleId, boundary),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['modules-map-at-date'] });
      setSaveError(null);
      exitDrawMode();
    },
    onError: (e: Error) => setSaveError(e.message ?? f.saveError),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ loteId }: { loteId: string }) =>
      patchLoteBoundary(loteId, { type: 'Polygon', coordinates: [] }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lotes-map'] }),
  });

  const deleteModuleMutation = useMutation({
    mutationFn: (moduleId: string) => deleteModule(moduleId),
    onSuccess: () => {
      setSelectedId(null);
      qc.invalidateQueries({ queryKey: ['modules-map-at-date'] });
      qc.invalidateQueries({ queryKey: ['investor-modules'] });
    },
    onError: (e: any) => window.alert(e?.response?.data?.error === 'MODULE_HAS_CHILDREN' ? t.terrain.deleteModuleHasChildren : t.terrain.deleteModuleError),
  });

  const deleteModuleBoundaryMutation = useMutation({
    mutationFn: ({ moduleId }: { moduleId: string }) =>
      patchModuleBoundary(moduleId, { type: 'Polygon', coordinates: [] }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['modules-map-at-date'] }),
  });

  // ── Features del mapa — lotes y módulos siempre juntos (unificados) ───────
  // El lote es el contenedor (contorno neutro, kind: 'lote') y sus módulos
  // se pintan encima coloreados por estado (kind: 'module', default) — ver
  // MapView.colorFor / LOTE_* constants.
  const catalogName = useCallback((cropId?: string) => cropId ? cropsCatalog.find(c => c.cropId === cropId)?.name : undefined, [cropsCatalog]);
  // El nombre guardado en el lote/zona es el actual; en otra fecha solo vale el cultivo resuelto por ciclo.
  // Cultivo + ciclo ("Opo · PRUEBA2") — el ciclo solo se conoce para la vista de hoy o el ciclo resuelto por fecha.
  const withCycle = (crop: string | undefined, ciclo?: string) => (crop && ciclo ? `${crop} · ${ciclo}` : crop);
  const loteCropName = useCallback((l: { cropId?: string; cultivo?: string; cropCycleLabel?: string }) =>
    withCycle(cropLabel(catalogName(l.cropId) ?? (isHistoric ? undefined : l.cultivo)), l.cropCycleLabel), [catalogName, isHistoric]);
  const moduleCropName = useCallback((m: FarmModule) =>
    withCycle(cropLabel(catalogName(m.cropId) ?? (isHistoric ? undefined : m.cropName)), m.cropCycleLabel), [catalogName, isHistoric]);
  const features: MapFeature[] = useMemo(() => {
    const loteFeatures: MapFeature[] = lotes.map(l => ({
      id: l.loteId,
      name: l.name,
      latitude: l.latitude,
      longitude: l.longitude,
      boundary: l.boundary,
      kind: 'lote',
      // Solo el tema de etapa colorea también al lote (un lote sin zonas es su propia zona).
      fillColor: fillTheme === 'stage' || fillTheme === 'custom'
        ? fillColorForTheme(fillTheme, { stage: stagesByLote[l.loteId]?.stage, displayColor: l.displayColor })
        : undefined,
      label: { name: l.name, crop: loteCropName(l), hectares: l.hectareas },
      faded: isClosedCycleLook(l, selectedDate, today),
      popupHtml: `<b>${l.name}</b>${loteCropName(l) ? `<br/>${loteCropName(l)}` : ''}`,
    }));

    const moduleFeatures: MapFeature[] = modules.map(m => {
      const crop = m.cropId ? cropsCatalog.find(c => c.cropId === m.cropId) : undefined;
      const pattern = crop ? cropPatternMap[crop.cropId] : undefined;
      const closed = isClosedCycleLook(m, selectedDate, today);
      const ndviEntry = ndviLayerOn ? ndviBatch?.results.find(r => r.moduleId === m.moduleId) : undefined;

      return {
        id: m.moduleId,
        name: m.moduleCode,
        boundary: m.boundary,
        status: m.status,
        kind: 'module',
        // Relleno: NDVI cuando la capa está activa, si no el color por status
        // (comportamiento previo, undefined = MapView usa STATUS_COLORS).
        fillColor: fillColorForTheme(fillTheme, {
          ndviMean: ndviEntry?.ndviStats?.mean,
          ndviRange,
          stage: stagesByLote[m.loteId]?.stage,
          displayColor: m.displayColor,
        }),
        label: { name: m.name || m.moduleCode, code: m.moduleCode, crop: moduleCropName(m), hectares: m.areaHa },
        // Borde: patrón de cultivo, siempre visible independiente del relleno.
        strokeColor: pattern?.colorHex,
        strokeDashArray: closed ? CLOSED_CYCLE_DASH : pattern?.dashArray,
        faded: closed,
        popupHtml:
          `<b>${m.moduleCode}</b>${m.name ? ` — ${m.name}` : ''}`
          + (crop ? `<br/>${t.terrain.crop}: ${crop.name}` : '')
          + (closed ? `<br/>${t.terrain.lastClosedCycle}${m.cropCycleLabel ? ` (${m.cropCycleLabel})` : ''}` : '')
          + (m.areaHa ? `<br/>${t.terrain.areaHa}: ${m.areaHa.toFixed(2)}` : '')
          + `<br/>${t.terrain.status[m.status]}`
          + (ndviLayerOn && ndviEntry?.ndviStats ? `<br/>NDVI: ${ndviEntry.ndviStats.mean.toFixed(2)}` : ''),
      };
    });

    return [...loteFeatures, ...moduleFeatures];
  }, [lotes, modules, t.terrain, cropsCatalog, cropPatternMap, ndviLayerOn, fillTheme, ndviRange, ndviBatch, stagesByLote, selectedDate, today, loteCropName, moduleCropName]);
  const hasClosedCycle = features.some(f => f.faded);

  // Área en vivo del polígono que se está trazando — recalculada en cada
  // vértice agregado o arrastrado (drawPoints ya llega actualizado por MapView).
  const themeLegend = fillThemeLegend(fillTheme, ndviRange);
  const fillThemeName = (th: FillTheme) =>
    th === 'ndvi' ? f.fillThemeNdvi
    : th === 'stage' ? f.fillThemeStage
    : th === 'custom' ? f.fillThemeCustom
    : f.fillThemeStatus;
  const legendLabel = (e: { ns: 'map' | 'crop'; key: string; params?: Record<string, string> }) => {
    const template = ((e.ns === 'crop' ? t.crop : f) as Record<string, string>)[e.key] ?? e.key;
    return Object.entries(e.params ?? {}).reduce((txt, [k, v]) => txt.replace(`{${k}}`, v), template);
  };
  const labelFieldName = (k: keyof LabelFields) =>
    ({ name: f.labelFieldName, crop: f.labelFieldCrop, hectares: f.labelFieldHectares, code: f.labelFieldCode }[k]);
  const drawAreaHa = useMemo(() => geodesicAreaHa(drawPoints), [drawPoints]);

  const isLoading = loadingLotes || loadingModules;
  const selectedLote = lotes.find(l => l.loteId === selectedId);
  const selectedModule = modules.find(m => m.moduleId === selectedId);
  // Una zona solo se traza dentro de su zona padre (si tiene) o de su lote, salvo las de pase de lista.
  const drawBounds = useMemo(() => {
    // Una zona marcada para pasar lista (entradas, oficinas…) puede ir en cualquier parte del rancho.
    if (!selectedModule || selectedModule.attendanceZone) return null;
    const parent = selectedModule.parentModuleId ? modules.find(m => m.moduleId === selectedModule.parentModuleId) : undefined;
    const container = parent?.boundary ?? lotes.find(l => l.loteId === selectedModule.loteId)?.boundary;
    const ring = container?.coordinates?.[0];
    return ring?.length ? ring.map(([lng, lat]) => [lat, lng] as [number, number]) : null;
  }, [selectedModule, modules, lotes]);
  const selected = selectedModule ?? selectedLote;
  const coordText = (v?: number | null) => (v != null ? String(v) : '—');
  const farmModulesDisabled = tenantConfigData?.effective['modules.useFarmModules']?.value === false;
  // Se crea desde un lote o una zona de primer nivel; una subzona no admite hijas.
  const canCreateZone = !!selected && !selectedModule?.parentModuleId && !!activeProjectId && !farmModulesDisabled;

  // ── Acciones ──────────────────────────────────────────────────────────────
  // `editExisting`: carga los vertices de la geocerca guardada para modificarla
  // (arrastrar, insertar, borrar). Sin el, empieza un trazo vacio.
  function enterDrawMode(editExisting = false) {
    const seed = editExisting ? boundaryToVertices((selectedModule ?? selectedLote)?.boundary) : [];
    setEditSeed(seed.length ? seed : undefined);
    setDrawPoints(seed);
    setDrawMode(true);
    setSaveError(null);
  }

  function exitDrawMode() {
    setEditSeed(undefined);
    setDrawMode(false);
    setDrawPoints([]);
  }

  // "Ver en mapa" desde Zonas: `?zoneId=<moduleId|código>` selecciona (y por tanto
  // centra) la zona. Se consume una sola vez, cuando ya cargaron los módulos.
  useEffect(() => {
    const zoneParam = searchParams.get('zoneId');
    if (!zoneParam || loadingModules) return;
    const target = modules.find(m => m.moduleId === zoneParam)
      ?? modules.find(m => m.moduleCode.toLowerCase() === zoneParam.toLowerCase());
    if (!target) return;
    setSelectedId(target.moduleId);
    // Encuadra la zona (o, sin geocerca, su lote) con el zoom que le corresponde.
    const ring = target.boundary?.coordinates?.[0]
      ?? lotes.find(l => l.loteId === target.loteId)?.boundary?.coordinates?.[0];
    if (ring?.length) {
      const lats = ring.map(([, lat]) => lat), lngs = ring.map(([lng]) => lng);
      const sw: [number, number] = [Math.min(...lats), Math.min(...lngs)];
      const ne: [number, number] = [Math.max(...lats), Math.max(...lngs)];
      setRecenterTo({ center: [(sw[0] + ne[0]) / 2, (sw[1] + ne[1]) / 2], bounds: [sw, ne] });
    }
    setCollapsedLotes(prev => { const n = new Set(prev); n.delete(target.loteId); return n; });
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('zoneId'); return n; }, { replace: true });
  }, [modules, lotes, loadingModules, searchParams, setSearchParams]);

  // Auto-selección + auto drawMode para el lote recién creado en FieldPage
  // (ver comentario arriba de searchParams). Se consume una sola vez — si el
  // usuario cancela o guarda y luego vuelve a entrar manualmente a drawMode,
  // no queremos reabrirlo solo porque el query param sigue en la URL.
  useEffect(() => {
    if (autoDrawConsumedRef.current) return;
    const targetLoteId = searchParams.get('loteId');
    const shouldDraw = searchParams.get('draw') === '1';
    const shouldModules = searchParams.get('modules') === '1';
    if (!targetLoteId || (!shouldDraw && !shouldModules)) return;
    if (loadingLotes) return; // esperar a que cargue la lista antes de buscar el lote
    const target = lotes.find(l => l.loteId === targetLoteId);
    if (!target) return; // el lote nuevo aún no llegó en esta carga — no marcar como consumido
    autoDrawConsumedRef.current = true;
    setSelectedId(target.loteId);
    if (shouldDraw) enterDrawMode(); else setModuleFormOpen(true);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('loteId');
      next.delete('draw');
      next.delete('modules');
      return next;
    }, { replace: true });
  }, [lotes, loadingLotes, searchParams, setSearchParams]);

  // Guarda de descarte — cualquier salida de drawMode con puntos sin guardar pasa por confirmación.
  function requestExitDrawMode(after?: () => void) {
    if (drawPoints.length > 0) {
      setPendingExitAction(() => after ?? null);
      setDiscardConfirmOpen(true);
      return;
    }
    exitDrawMode();
    after?.();
  }

  function confirmDiscard() {
    exitDrawMode();
    setDiscardConfirmOpen(false);
    pendingExitAction?.();
    setPendingExitAction(null);
  }

  function cancelDiscard() {
    setDiscardConfirmOpen(false);
    setPendingExitAction(null);
  }

  function handleSaveBoundary() {
    if (!selectedId || drawPoints.length < 3) return;
    // Cerrar el anillo: GeoJSON exige primer punto === último
    const ring = [...drawPoints, drawPoints[0]];
    const coords = ring.map(([lat, lng]) => [lng, lat]); // GeoJSON es [lng, lat]
    const boundary: GeoJSONPolygon = { type: 'Polygon', coordinates: [coords] };
    if (selectedModule) {
      saveModuleBoundaryMutation.mutate({ moduleId: selectedId, boundary });
    } else {
      saveMutation.mutate({ loteId: selectedId, boundary });
    }
  }

  function handleDeleteBoundary() {
    if (!selectedId) return;
    if (selectedModule) {
      deleteModuleBoundaryMutation.mutate({ moduleId: selectedId });
    } else {
      deleteMutation.mutate({ loteId: selectedId });
    }
  }

  // ── Identidad estable para el onSelect que se pasa a MapView ───────────────
  // MapView incluye `onSelect` en el dependency array del efecto que reconstruye
  // las capas de Leaflet y hace fitBounds. Si le pasamos un arrow inline, cambia
  // de identidad en cada render de MapPage (ej. cada punto agregado al trazar
  // dispara setDrawPoints → re-render → MapView refit/rebuild en medio del trazo).
  // drawMode y requestExitDrawMode se leen vía refs sincronizados por efecto para
  // que el callback siempre vea el valor actual sin necesitar deps que rompan su
  // identidad (requestExitDrawMode en sí se redefine cada render porque cierra
  // sobre drawPoints, así que también necesita pasar por ref).
  const drawModeRef = useRef(drawMode);
  const requestExitDrawModeRef = useRef(requestExitDrawMode);
  useEffect(() => {
    drawModeRef.current = drawMode;
    requestExitDrawModeRef.current = requestExitDrawMode;
  });

  // Esc cancela (con la confirmacion de descarte) y Enter guarda si hay >= 3 puntos.
  const handleSaveBoundaryRef = useRef(handleSaveBoundary);
  const drawPointsRef = useRef(drawPoints);
  const drawTooLargeRef = useRef(false);
  useEffect(() => {
    handleSaveBoundaryRef.current = handleSaveBoundary;
    drawPointsRef.current = drawPoints;
    drawTooLargeRef.current = drawAreaHa > MAX_REASONABLE_BOUNDARY_HA;
  });
  useEffect(() => {
    if (!drawMode) return;
    const onKey = (ev: KeyboardEvent) => {
      const tag = (ev.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (ev.key === 'Escape') {
        requestExitDrawModeRef.current();
      } else if (ev.key === 'Enter' && drawPointsRef.current.length >= 3 && !drawTooLargeRef.current) {
        handleSaveBoundaryRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawMode]);

  const handleMapSelect = useCallback((id: string) => {
    if (drawModeRef.current) {
      requestExitDrawModeRef.current(() => setSelectedId(id));
      return;
    }
    setSelectedId(id);
  }, []);

  const { data: ranchos = [] } = useQuery({ queryKey: ['ranchos-map-names'], queryFn: fetchRanchos, enabled: !activeRanchoId });
  const ranchoNameById = useMemo(() => new Map(ranchos.map(r => [r.ranchoId, r.name])), [ranchos]);
  const norm = (v?: string | null) => (v ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const q = norm(listSearch.trim());
  const moduleMatches = (m: FarmModule) =>
    [m.moduleCode, m.name, m.cropName, t.terrain.status[m.status]].some(v => norm(v).includes(q));
  const visibleLotes = q
    ? lotes.filter(l => norm(l.name).includes(q) || norm(l.cultivo).includes(q)
        || modules.some(m => m.loteId === l.loteId && moduleMatches(m)))
    : lotes;
  const toggleLote = (id: string) =>
    setCollapsedLotes(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className={s.root}>
      {/* ── Panel lateral ───────────────────────────────────────────── */}
      <div className={`${s.sidebar} ${sidebarOpen ? '' : s.sidebarCollapsed}`}>
        <Button
          appearance="subtle" size="small"
          className={s.sidebarToggle}
          icon={sidebarOpen ? <ChevronLeftRegular /> : <ChevronRightRegular />}
          aria-label={sidebarOpen ? f.collapseSidebar : f.expandSidebar}
          aria-expanded={sidebarOpen}
          title={sidebarOpen ? f.collapseSidebar : f.expandSidebar}
          onClick={() => setSidebarOpen(o => !o)}
        />
        <div className={s.sidebarHeader}>
          <Text size={500} weight="semibold">{f.title}</Text>

          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            {isLoading
              ? f.loading
              : `${lotes.length} ${f.lotesCount}`
                + (modules.length ? ` · ${modules.length} ${t.terrain.modules}` : '')}
          </Text>
        </div>

        <div style={{ padding: '8px 16px' }}>
          <Input
            size="small" style={{ width: '100%' }} value={listSearch}
            placeholder={f.searchPlaceholder} aria-label={f.searchPlaceholder}
            onChange={(_, d) => setListSearch(d.value)}
          />
        </div>

        {isLoading && <Spinner size="small" style={{ margin: '24px auto' }} />}

        {/* Árbol Lote → Módulos: cada lote es el contenedor, sus módulos (si
            hay proyecto activo y ya se cargaron) se listan indentados debajo —
            clic en cualquiera de los dos selecciona esa zona en el mapa. */}
        <div className={s.list}>
          {!isLoading && visibleLotes.map((lote, idx) => {
            const loteModules = modules.filter(m => m.loteId === lote.loteId && (!q || moduleMatches(m) || norm(lote.name).includes(q)));
            const expanded = !!q || !collapsedLotes.has(lote.loteId);
            const showRancho = !activeRanchoId && (idx === 0 || visibleLotes[idx - 1].ranchoId !== lote.ranchoId);
            return (
              <div key={lote.loteId}>
                {showRancho && (
                  <Text size={200} weight="semibold" style={{ display: 'block', padding: '8px 16px 2px', color: tokens.colorNeutralForeground3 }}>
                    {ranchoNameById.get(lote.ranchoId) ?? f.ranchoUnknown}
                  </Text>
                )}
                <div
                  className={`${s.item} ${lote.loteId === selectedId ? s.itemActive : ''}`}
                  onClick={() => {
                    if (drawMode) { requestExitDrawMode(() => setSelectedId(lote.loteId)); return; }
                    setSelectedId(lote.loteId);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {loteModules.length > 0 && (
                      <Button
                        size="small" appearance="transparent" style={{ minWidth: 0, padding: 0 }}
                        aria-label={expanded ? f.collapseLote : f.expandLote} aria-expanded={expanded}
                        icon={expanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                        onClick={e => { e.stopPropagation(); toggleLote(lote.loteId); }}
                      />
                    )}
                    <Text weight="semibold" size={300}>{lote.name}</Text>
                  </div>
                  {loteCropName(lote) && (
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{loteCropName(lote)}</Text>
                  )}
                  <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                    {lote.latitude && lote.longitude && (
                      <Badge size="small" appearance="tint" color="success">{f.badgeGPS}</Badge>
                    )}
                    {lote.boundary?.coordinates?.length ? (
                      <Badge size="small" appearance="tint" color="brand">{f.badgePoly}</Badge>
                    ) : (
                      <Badge size="small" appearance="tint" color="subtle">{f.badgeNoPoly}</Badge>
                    )}
                  </div>
                </div>

                {expanded && orderedModules(loteModules).map(({ mod, depth }) => (
                  <div
                    key={mod.moduleId}
                    style={depth ? { paddingLeft: '52px' } : undefined}
                    className={`${s.moduleItem} ${mod.moduleId === selectedId ? s.itemActive : ''}`}
                    onClick={() => {
                      if (drawMode) { requestExitDrawMode(() => setSelectedId(mod.moduleId)); return; }
                      setSelectedId(mod.moduleId);
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {depth > 0 && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>↳</Text>}
                      <span className={s.legendDot} style={{ background: STATUS_COLORS[mod.status] }} />
                      <Text weight="semibold" size={300}>{mod.moduleCode}</Text>
                    </div>
                    {moduleCropName(mod) && (
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{moduleCropName(mod)}</Text>
                    )}
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                      {t.terrain.status[mod.status]}
                      {mod.areaHa ? ` · ${mod.areaHa.toFixed(2)} ha` : ''}
                    </Text>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {drawMode && drawPoints.length > 0 && (
          <div style={{ padding: '12px 16px', borderTop: `1px solid ${tokens.colorNeutralStroke2}`, overflowY: 'auto', maxHeight: '260px' }}>
            <Text size={200} weight="semibold">{f.drawPoints}</Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
              {drawPoints.map(([lat, lon], i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: vertexColor(i), flexShrink: 0 }} />
                  <Text size={200} style={{ fontFamily: 'monospace', color: tokens.colorNeutralForeground2 }}>
                    {lat.toFixed(6)}, {lon.toFixed(6)}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        )}

        {(selectedLote || selectedModule) && (
          <div className={s.infoCard}>
            <Text size={300} weight="semibold">{selectedModule ? selectedModule.moduleCode : selectedLote!.name}</Text>
            <div className={s.infoRow}>
              <Text size={200}>{f.fieldLatitude}</Text>
              <Text size={200} className={s.mono}>{coordText((selectedModule ?? selectedLote)?.latitude)}</Text>
            </div>
            <div className={s.infoRow}>
              <Text size={200}>{f.fieldLongitude}</Text>
              <Text size={200} className={s.mono}>{coordText((selectedModule ?? selectedLote)?.longitude)}</Text>
            </div>
            {(selectedModule ? moduleCropName(selectedModule) : loteCropName(selectedLote!)) && (
              <div className={s.infoRow}>
                <Text size={200}>{t.terrain.crop}</Text>
                <Text size={200}>{selectedModule ? moduleCropName(selectedModule) : loteCropName(selectedLote!)}</Text>
              </div>
            )}
            {selectedModule && (
              <div className={s.infoRow}>
                <Text size={200}>{t.terrain.legend}</Text>
                <Text size={200}>{t.terrain.status[selectedModule.status]}</Text>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Mapa ────────────────────────────────────────────────────── */}
      <div className={s.mapContainer}>
        <MapView
          className={s.mapEl}
          features={features}
          selectedId={selectedId}
          onSelect={handleMapSelect}
          satellite={satellite}
          drawMode={drawMode}
          onDrawPointsChange={setDrawPoints}
          fitKey={activeRanchoId ?? 'all'}
          initialDrawPoints={editSeed}
          drawBounds={drawBounds}
          recenterTo={recenterTo}
          showLabels={labelPrefs.enabled}
          labelFields={labelPrefs.fields}
        />

        {/* Slider de vigencia — mientras se traza una geocerca se oculta: no
            hay caso de uso para navegar el tiempo y editar el polígono a la vez. */}
        {!drawMode && (
          <div className={s.dateSliderBar}>
            <div className={s.panelSection}>
            <Text size={200} style={{ whiteSpace: 'nowrap' }}>{f.dateSliderLabel}: {sliderInput}</Text>
            <input
              type="range"
              style={{ flex: 1 }}
              min={new Date(sliderMinDate).getTime()}
              max={new Date(today).getTime()}
              value={new Date(sliderInput).getTime()}
              onChange={e => handleSliderChange(new Date(Number(e.target.value)).toISOString().split('T')[0])}
            />
            </div>
            <div className={s.panelSection}>
            <Field label={f.fillThemeLabel} orientation="horizontal">
              <Dropdown
                size="small"
                aria-label={f.fillThemeLabel}
                value={fillThemeName(fillTheme)}
                selectedOptions={[fillTheme]}
                onOptionSelect={(_, d) => { if (d.optionValue && isFillTheme(d.optionValue)) setFillTheme(d.optionValue); }}
                style={{ minWidth: '120px' }}
              >
                {FILL_THEMES.filter(th => th !== 'ndvi' || !ndviKnownOff).map(th => <Option key={th} value={th}>{fillThemeName(th)}</Option>)}
              </Dropdown>
            </Field>
            {ndviLayerOn && (
              <Field label={f.ndviScale} orientation="horizontal">
                <Dropdown
                  size="small"
                  aria-label={f.ndviScale}
                  value={ndviScale === 'DYNAMIC' ? f.ndviScaleDynamic : f.ndviScaleFixed}
                  selectedOptions={[ndviScale]}
                  onOptionSelect={(_, d) => { if (d.optionValue === 'FIXED' || d.optionValue === 'DYNAMIC') changeNdviScale(d.optionValue); }}
                  style={{ minWidth: '110px' }}
                >
                  <Option value="FIXED">{f.ndviScaleFixed}</Option>
                  <Option value="DYNAMIC">{f.ndviScaleDynamic}</Option>
                </Dropdown>
              </Field>
            )}
            </div>
            <div className={s.panelSection}>
            <Switch
              checked={labelPrefs.enabled}
              onChange={(_, d) => updateLabelPrefs({ ...labelPrefs, enabled: d.checked })}
              label={f.labelsToggle}
            />
            {labelPrefs.enabled && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {(['name', 'crop', 'hectares', 'code'] as const).map(k => (
                  <Checkbox
                    key={k}
                    size="medium"
                    checked={labelPrefs.fields[k]}
                    label={labelFieldName(k)}
                    onChange={(_, d) => updateLabelPrefs({
                      ...labelPrefs, fields: { ...labelPrefs.fields, [k]: d.checked === true },
                    })}
                  />
                ))}
              </div>
            )}
            </div>
          </div>
        )}

        <div className={s.toolbar}>
          <Switch
            checked={satellite}
            onChange={(_, d) => setSatellite(d.checked)}
            label={f.satellite}
          />

          {canWriteTerrain && (
            <div className={s.toolbarButtons}>
              {selectedLote && !drawMode && (
                <Button appearance="secondary" icon={<EditRegular />} onClick={() => setEditLoteOpen(true)}>
                  {t.terrain.editLoteBtn}
                </Button>
              )}
              {/* Crear cuelga del lote o de la zona seleccionada (en una zona crea una
                  subzona); una subzona ya es el último nivel, no admite más. */}
              {!drawMode && (
                <Button
                  appearance="primary"
                  icon={<AddRegular />}
                  onClick={() => setModuleFormOpen(true)}
                  disabled={!canCreateZone}
                  title={farmModulesDisabled ? t.terrain.farmModulesDisabledTooltip : (!activeProjectId ? t.terrain.noProjectSelectedHint : undefined)}
                >
                  {t.terrain.createModuleBtn}
                </Button>
              )}
              {selectedModule && !drawMode && (
                <Button appearance="secondary" icon={<EditRegular />} onClick={() => setEditModuleId(selectedModule.moduleId)}>
                  {t.terrain.editModuleBtn}
                </Button>
              )}
              {selectedModule && !drawMode && (
                <Button
                  appearance="subtle"
                  icon={<Delete24Regular />}
                  disabled={deleteModuleMutation.isPending}
                  onClick={() => { if (window.confirm(`${t.terrain.deleteModuleConfirm} ${selectedModule.moduleCode}${selectedModule.name ? ` — ${selectedModule.name}` : ''}?`)) deleteModuleMutation.mutate(selectedModule.moduleId); }}
                >
                  {t.terrain.deleteModuleBtn}
                </Button>
              )}

              {/* Trazar geocerca aplica a lotes y zonas: ambos reutilizan el mismo drawMode de MapView. */}
              {selected && !drawMode && (
                <>
                  <Button appearance="primary" icon={<Edit24Regular />} onClick={() => enterDrawMode(true)}>
                    {f.drawBtn}
                  </Button>
                  {selected.boundary?.coordinates?.length ? (
                    <Button
                      appearance="subtle"
                      icon={<Delete24Regular />}
                      onClick={handleDeleteBoundary}
                      disabled={deleteMutation.isPending || deleteModuleBoundaryMutation.isPending}
                    >
                      {selectedModule ? t.terrain.deleteModuleBoundaryBtn : f.deleteBtn}
                    </Button>
                  ) : null}
                </>
              )}

              {drawMode && (
                <>
                  <Button
                    appearance="primary"
                    icon={<Save24Regular />}
                    disabled={drawPoints.length < 3 || drawAreaHa > MAX_REASONABLE_BOUNDARY_HA || saveMutation.isPending || saveModuleBoundaryMutation.isPending}
                    onClick={handleSaveBoundary}
                  >
                    {(saveMutation.isPending || saveModuleBoundaryMutation.isPending) ? f.saving : f.saveBtn}
                  </Button>
                  <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={() => requestExitDrawMode()}>
                    {f.cancelBtn}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {ndviLayerOn && ndviSceneDate && (
          <div className={s.infoBar} data-testid="ndvi-scene-date">
            <span>{f.ndviSceneDate}: {formatSceneDate(ndviSceneDate)}</span>
          </div>
        )}

        {ndviNotice && (
          <div className={s.infoBar} data-testid="ndvi-disabled-notice">
            <span>{f.ndviDisabled}</span>
          </div>
        )}

        {(modules.length > 0 || !!themeLegend || hasClosedCycle) && (
          <div className={s.legend}>
            <Text size={200} weight="semibold">{themeLegend ? fillThemeName(fillTheme) : t.terrain.legend}</Text>
            {themeLegend
              ? themeLegend.map(e => (
                <div key={e.key} className={s.legendRow}>
                  <span className={s.legendDot} style={{ background: e.color }} />
                  <span>{legendLabel(e)}</span>
                </div>
              ))
              : LEGEND_ORDER.map(status => (
                <div key={status} className={s.legendRow}>
                  <span className={s.legendDot} style={{ background: STATUS_COLORS[status] }} />
                  <span>{t.terrain.status[status]}</span>
                </div>
              ))}
            {hasClosedCycle && (
              <div className={s.legendRow} data-testid="legend-closed-cycle">
                <span className={s.legendDot} style={{ border: `2px dotted ${tokens.colorNeutralStroke1}`, opacity: 0.5 }} />
                <span>{t.terrain.lastClosedCycle}</span>
              </div>
            )}
          </div>
        )}

        {drawMode && (
          <div className={s.infoBar}>
            <Location24Regular style={{ color: tokens.colorBrandForeground1 }} />
            <span>
              {drawPoints.length === 0
                ? f.drawHint
                : `${drawPoints.length} ${f.drawPoints}${drawPoints.length >= 3 ? ` — ${f.drawReady} — ${drawAreaHa.toFixed(2)} ha` : ''}`}
            </span>
          </div>
        )}

        {saveError && (
          <div
            className={s.errorBox}
            style={{ position: 'absolute', bottom: '60px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}
          >
            ⚠ {saveError}
          </div>
        )}

        {drawMode && drawAreaHa > MAX_REASONABLE_BOUNDARY_HA && (
          <div
            className={s.errorBox}
            style={{ position: 'absolute', bottom: '60px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}
          >
            ⚠ {f.areaTooLargeError.replace('{area}', drawAreaHa.toFixed(0))}
          </div>
        )}

        {moduleFormOpen && selected && (
          <FarmModuleFormDialog
            open={moduleFormOpen}
            loteId={selectedModule ? selectedModule.loteId : selectedLote!.loteId}
            loteName={(selectedModule ? lotes.find(l => l.loteId === selectedModule.loteId) : selectedLote)?.name}
            defaultParentId={selectedModule?.moduleId}
            onClose={() => setModuleFormOpen(false)}
            activeProjectId={activeProjectId}
            loteModules={modules.filter(m => m.loteId === (selectedModule ? selectedModule.loteId : selectedLote!.loteId))}
          />
        )}

        {editLoteOpen && selectedLote && (
          <LoteFormDialog
            open={editLoteOpen}
            initial={selectedLote}
            onClose={() => setEditLoteOpen(false)}
          />
        )}

        {editModuleId && modules.find(m => m.moduleId === editModuleId) && (
          <FarmModuleFormDialog
            open={!!editModuleId}
            loteId={modules.find(m => m.moduleId === editModuleId)!.loteId}
            loteName={lotes.find(l => l.loteId === modules.find(m => m.moduleId === editModuleId)!.loteId)?.name}
            initial={modules.find(m => m.moduleId === editModuleId)}
            onClose={() => setEditModuleId(null)}
            activeProjectId={activeProjectId}
            loteModules={modules.filter(m => m.loteId === modules.find(x => x.moduleId === editModuleId)!.loteId)}
          />
        )}

        <Dialog open={discardConfirmOpen} onOpenChange={(_, d) => !d.open && cancelDiscard()} modalType="alert">
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{f.discardTitle}</DialogTitle>
              <DialogContent><Text>{f.discardBody}</Text></DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={cancelDiscard}>{f.discardCancel}</Button>
                <Button appearance="primary" style={{ backgroundColor: tokens.colorStatusDangerBackground3 }} onClick={confirmDiscard}>
                  {f.discardConfirm}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </div>
    </div>
  );
}
